import { gql } from '@apollo/client';
import type { TypedDocumentNode } from '@apollo/client';
import { useEffect, useState } from 'react';

import type { FiatWalletKycState } from '#src/__generated__/globalTypes.ts';
import { fiatAccounts } from '#src/contexts/currentUser/queries.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';
import { isRefusedKycStatus } from '#src/lib/mangopay.ts';

import type {
  CurrentUserFiatAccountsQuery,
  CurrentUserFiatAccountsQueryVariables,
} from './__generated__/useHostedVerificationRefusal.graphql.ts';

const POLL_INTERVAL = 5000;
export const STALE_REFUSAL_GRACE_PERIOD_MS = 60_000;

const CURRENT_USER_FIAT_ACCOUNTS_QUERY = gql`
  query CurrentUserFiatAccountsQuery {
    currentUser {
      id
      slug
      ...CurrentUseProvider_fiatAccounts
    }
  }
  ${fiatAccounts}
` as TypedDocumentNode<
  CurrentUserFiatAccountsQuery,
  CurrentUserFiatAccountsQueryVariables
>;

const NEVER_STOP_POLLING = [] as const;

type Params = {
  stopPollingOn?: readonly FiatWalletKycState[];
  skip?: boolean;
};

export const useHostedVerificationRefusal = ({
  stopPollingOn = NEVER_STOP_POLLING,
  skip = false,
}: Params = {}) => {
  const { kycStatus } = useFiatBalance();
  const isRefusedStatusNow = isRefusedKycStatus(kycStatus);

  const [baselineStatus, setBaselineStatus] = useState(kycStatus);
  const [gracePeriodElapsed, setGracePeriodElapsed] = useState(false);
  const [wasSkipped, setWasSkipped] = useState(skip);

  if (skip !== wasSkipped) {
    setWasSkipped(skip);
    setBaselineStatus(kycStatus);
    setGracePeriodElapsed(false);
  }

  useEffect(() => {
    if (skip) return undefined;

    const timeout = setTimeout(
      () => setGracePeriodElapsed(true),
      STALE_REFUSAL_GRACE_PERIOD_MS
    );
    return () => clearTimeout(timeout);
  }, [skip]);

  const hasStatusChanged = kycStatus !== baselineStatus;
  const isRefusalTrusted =
    !isRefusedKycStatus(baselineStatus) ||
    hasStatusChanged ||
    gracePeriodElapsed;
  const isRefused = isRefusedStatusNow && isRefusalTrusted;

  const hasReachedStopStatus = !!kycStatus && stopPollingOn.includes(kycStatus);

  useQuery(CURRENT_USER_FIAT_ACCOUNTS_QUERY, {
    pollInterval: POLL_INTERVAL,
    skip: skip || hasReachedStopStatus || isRefused,
  });

  return isRefused;
};
